import CModal from "@/shared/components/wrappers/modal/CModal.vue";
import { addIcon, sortIcon } from "common/defaultIcons";
import KnowledgeBaseCategoryDialog from "components/knowledge-base/KnowledgeBaseCategoryDialog.vue";
import KnowledgeBaseSortDialog from "components/knowledge-base/KnowledgeBaseSortDialog.vue";
import type {
  CategoryWithStubs,
  KnowledgeBaseCategoryPayload,
} from "models/knowledge-base/knowledge-base";
import { storeToRefs } from "pinia";
import { useQuasar } from "quasar";
import { useKnowledgeBaseStore } from "store/modules/knowledgeBaseStore";
import { useRouter } from "vue-router";

interface CategoryMenuItem {
  label: string;
  icon: string;
  onClick: () => void;
}

interface AxiosLikeError {
  response?: {
    data?: {
      errors?: { category?: string[] };
      message?: string;
    };
  };
}

export const useKnowledgeBaseCategoryActions = () => {
  const $q = useQuasar();
  const router = useRouter();
  const kbStore = useKnowledgeBaseStore();
  const { categories } = storeToRefs(kbStore);
  const { updateCategory, deleteCategory } = kbStore;

  const findCategory = (uuid: string): CategoryWithStubs | null =>
    categories.value.find((c) => c.uuid === uuid) ?? null;

  const openSortDialog = (): void => {
    $q.dialog({
      component: KnowledgeBaseSortDialog,
    });
  };

  const openEditDialog = (uuid: string): void => {
    const category = findCategory(uuid);
    if (!category) {
      return;
    }
    $q.dialog({
      component: KnowledgeBaseCategoryDialog,
      componentProps: { category },
    }).onOk(async (payload: KnowledgeBaseCategoryPayload) => {
      await updateCategory(uuid, payload);
    });
  };

  const showDeleteErrorDialog = (message: string): void => {
    $q.dialog({
      component: CModal,
      componentProps: {
        title: "Verwijderen mislukt",
        message,
        type: "negative",
        cancel: false,
        primaryLabel: "Ok",
      },
    });
  };

  const openDeleteDialog = (uuid: string): void => {
    const category = findCategory(uuid);
    if (!category) {
      return;
    }
    const itemCount = category.items?.length ?? 0;
    if (itemCount > 0) {
      $q.dialog({
        component: CModal,
        componentProps: {
          title: "Categorie bevat items",
          message: `Deze categorie bevat nog ${itemCount} items. Verplaats of verwijder de items eerst voordat je de categorie verwijdert.`,
          type: "warning",
          cancel: false,
          primaryLabel: "Ok",
        },
      });
      return;
    }
    $q.dialog({
      component: CModal,
      componentProps: {
        title: "Categorie verwijderen",
        message: `Weet je zeker dat je de categorie "${category.title}" wilt verwijderen?`,
        type: "negative",
        cancel: true,
        primaryLabel: "Verwijderen",
      },
    }).onOk(async () => {
      try {
        await deleteCategory(uuid);
      } catch (e: unknown) {
        const err = e as AxiosLikeError;
        const message =
          err?.response?.data?.errors?.category?.[0] ??
          err?.response?.data?.message ??
          "Kon categorie niet verwijderen";
        showDeleteErrorDialog(message);
      }
    });
  };

  const menuItems: CategoryMenuItem[] = [
    {
      label: "Indeling wijzigen",
      icon: sortIcon,
      onClick: openSortDialog,
    },
    {
      label: "Nieuw artikel",
      icon: addIcon,
      onClick: () => {
        router.push({ name: "knowledgeBaseItemAdd" });
      },
    },
  ];

  return {
    menuItems,
    openEditDialog,
    openDeleteDialog,
    openSortDialog,
  };
};
